import React, {forwardRef, ReactElement, useContext} from 'react';
import MuiAlert, {AlertProps} from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import {Snackbar} from '../../interfaces/SnackbarData';
import {SnackbarContext} from '../../shared/store';
import {getAlertSeverityColors} from '../../shared/constants/constants';
import successAlert from '../../assets/images/success-alert.png';
import infoAlert from '../../assets/images/info-alert-icon.png';
import failureAlert from '../../assets/images/failure-alert.png';
import {alertSx, boldSx, normalSx} from './styles';

// Props passed to SnackbarComponent
type Props = {
    id: number;
    open: boolean;
    boldAlertMsg: string;
    normalAlertMsg: ReactElement | string;
    severity: any;
    setInfoSnackbar: React.Dispatch<null | Snackbar>;
};

// Custom wrapper around MUI's Alert with fixed elevation + filled variant
const Alert = forwardRef<HTMLDivElement, AlertProps>((p, r) => (
    <MuiAlert elevation={4} ref={r} variant="filled" {...p} />));

/**
 * SnackbarComponent
 * Displays a dismissible alert message (success, error, info)
 * with bold + normal text styling and custom icons.
 */
export default function SnackbarComponent({id, open, boldAlertMsg, normalAlertMsg, severity, setInfoSnackbar}: Props) {
    const {removeSnackbar} = useContext(SnackbarContext);
    if (!open) return null; // Don't render if not open

    // Handles closing the Snackbar
    const handleClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return; // ignore clickaway dismissals
        if (severity === 'info') setInfoSnackbar(null); // clear "info" state
        removeSnackbar(id); // remove from context
    };

    // Get dynamic background/text color from severity
    const {backgroundColor, textColor} = getAlertSeverityColors(severity);

    return (<Alert
            data-testid="custom-alert"
            onClose={handleClose}
            severity={severity}
            iconMapping={{
                success: <img src={successAlert} alt="success"/>,
                error: <img src={failureAlert} alt="failure"/>,
                info: <img src={infoAlert} alt="info"/>,
            }}
            sx={alertSx(backgroundColor, textColor)}
        >
            {/* Bold + normal message segments */}
            SNACKBAR
            <Typography component="span" sx={boldSx}>{boldAlertMsg}</Typography>{' '}
            <Typography component="span" sx={normalSx}>{normalAlertMsg}</Typography>
        </Alert>);
}
